import { debounce, throttle } from 'lodash-es';
import React, { Component } from 'react';
import type { Merge } from 'types/utility';
import type { Props as InputProps } from './Input';
import Input from './Input';

interface State {
  value: string;
}

interface OwnProps {
  name: string;
  /** ms to wait before callback */
  wait: number;
  value: string;
  /** when changed, internal value state will reset to `props.value` */
  valueStateBuster?: string | boolean | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Specify which type of delay, defaults to debounce */
  delayType: 'debounce' | 'throttle';
}

type Props = Merge<InputProps, OwnProps>;

/**
 * InputWithDelay fires `onChange` callback every `wait` ms.
 *
 * To update component's `value`, set `valueStateBuster` to a new value.
 * ie `valueStateBuster={value === ''}`
 */
export default class InputWithDelay extends Component<Props, State> {
  static defaultProps = {
    wait: 200,
    value: '',
    delayType: 'debounce',
  };

  state = {
    value: this.props.value,
  };

  componentDidUpdate(prevProps: Props) {
    const hasRelevantValue = this.state.value !== this.props.value;
    const valueBusted = this.props.valueStateBuster !== prevProps.valueStateBuster;

    if (hasRelevantValue && valueBusted) {
      this.setState({ value: this.props.value });
    }
  }

  handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    this.setState({ value: event.target.value || '' });

    if (this.props.delayType === 'throttle') {
      this.throttledOnChange(event);
    } else {
      this.debouncedOnChange(event);
    }
  };

  throttledOnChange = throttle(event => this.props.onChange(event), this.props.wait);

  debouncedOnChange = debounce(event => this.props.onChange(event), this.props.wait);

  render() {
    const { onChange, valueStateBuster, delayType, ...rest } = this.props;
    return <Input {...rest} value={this.state.value} onChange={this.handleOnChange} />;
  }
}
